import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

const SAMPLE = `SELECT t.txn_seq, t.pay_dt, t.txn_amt, pye.pye_nm, cod.codes_lit
FROM txnsaction t
LEFT JOIN payee pye ON pye.pye_id = t.pye_id
LEFT JOIN codes cod ON cod.codes_id = t.codes_id
ORDER BY t.pay_dt DESC
LIMIT 50;`;

function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv =
    columns.join(",") + "\n" +
    rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function SqlWorkbench() {
  const [schema, setSchema] = useState(null);
  const [sql, setSql] = useState(SAMPLE);
  const [writeMode, setWriteMode] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const taRef = useRef(null);

  useEffect(() => {
    api.schema().then(setSchema).catch(() => {});
  }, []);

  const tables = schema?.tables || [];
  const routines = useMemo(() => schema?.routines || [], [schema]);

  // group routines by entity (everything before the first underscore)
  const routineGroups = useMemo(() => {
    const g = {};
    for (const r of routines) {
      const key = r.name.split("_")[0];
      (g[key] = g[key] || []).push(r);
    }
    return g;
  }, [routines]);

  const run = async () => {
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const r = await api.runSql(sql, writeMode);
      setResult(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  const insert = (snippet) => {
    setSql((prev) => (prev.trim() ? prev + "\n\n" + snippet : snippet));
    // give the new statement focus
    requestAnimationFrame(() => taRef.current?.focus());
  };

  return (
    <section className="wb">
      <aside className="wb-side card" data-testid="wb-schema">
        <p className="section-title" style={{ padding: "14px 16px 0" }}>Schema</p>

        <div className="wb-side-group">
          <p className="wb-side-label">Tables ({tables.length})</p>
          {tables.map((t) => (
            <details key={t.name} className="wb-tree">
              <summary>
                <span className="wb-tree-name">{t.name}</span>
                <button
                  className="wb-mini"
                  title="Insert SELECT for this table"
                  onClick={(e) => {
                    e.preventDefault();
                    insert(`SELECT * FROM ${t.name} LIMIT 100;`);
                  }}
                >
                  ↘
                </button>
              </summary>
              <ul className="wb-cols">
                {t.columns.map((c) => (
                  <li key={c.name}>
                    <span className="wb-col-name">{c.name}</span>
                    <span className="wb-col-type">{c.type}</span>
                    {c.key === "PRI" && <span className="wb-tag pk">PK</span>}
                    {c.key === "UNI" && <span className="wb-tag uk">UQ</span>}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>

        <div className="wb-side-group">
          <p className="wb-side-label">Procedures ({routines.length})</p>
          {Object.entries(routineGroups).map(([entity, procs]) => (
            <details key={entity} className="wb-tree">
              <summary>
                <span className="wb-tree-name">{entity}</span>
                <span className="wb-tree-count">{procs.length}</span>
              </summary>
              <ul className="wb-cols">
                {procs.map((p) => (
                  <li key={p.name}>
                    <button
                      className="wb-routine"
                      onClick={() => insert(`CALL ${p.name}(/* args */);`)}
                      title="Insert CALL skeleton"
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </aside>

      <div className="wb-main">
        <div className="card wb-editor-card">
          <div className="wb-editor-bar">
            <span className="section-title" style={{ margin: 0 }}>SQL editor</span>
            <span className="wb-hint">⌘/Ctrl+Enter to run</span>
            <label className="wb-toggle" title="Required to run anything other than SELECT/SHOW/DESCRIBE/EXPLAIN">
              <input
                type="checkbox"
                checked={writeMode}
                onChange={(e) => setWriteMode(e.target.checked)}
                data-testid="wb-writemode"
              />
              <span>Allow writes</span>
            </label>
            <button
              className="btn-primary wb-run"
              onClick={run}
              disabled={running || !sql.trim()}
              data-testid="wb-run"
            >
              {running ? "Running…" : "Run"}
            </button>
          </div>
          <textarea
            ref={taRef}
            className="wb-editor"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            placeholder="-- type SQL or click a table/proc in the sidebar"
            data-testid="wb-sql"
          />
        </div>

        <div className="card wb-results-card" data-testid="wb-results">
          {error && <div className="wb-error">⚠️  {error}</div>}

          {!error && !result && (
            <div className="empty" style={{ padding: "60px 20px" }}>
              <span className="big" aria-hidden>›_</span>
              Run a query to see results here.
            </div>
          )}

          {result && (
            <>
              <div className="wb-result-meta">
                <span className="wb-stat">
                  <b>{result.columns.length}</b> cols · <b>{result.rows.length}</b> rows
                </span>
                <span className="wb-stat muted">type: {result.statement_type}</span>
                <span className="wb-stat muted">took {result.took_ms} ms</span>
                {result.rowcount > 0 && (
                  <span className="wb-stat muted">affected: {result.rowcount}</span>
                )}
                {result.columns.length > 0 && result.rows.length > 0 && (
                  <button
                    className="btn-ghost"
                    style={{ marginLeft: "auto" }}
                    data-testid="wb-export-csv"
                    onClick={() =>
                      downloadCsv("query_result.csv", result.columns, result.rows)
                    }
                  >
                    ↓ Export CSV
                  </button>
                )}
              </div>

              {result.notes.length > 0 && (
                <div className="wb-notes">
                  {result.notes.map((n, i) => <div key={`n-${i}-${n.slice(0,8)}`}>· {n}</div>)}
                </div>
              )}

              {result.columns.length > 0 ? (
                <div className="wb-result-scroll">
                  <table className="txn-table wb-result-table">
                    <thead>
                      <tr>
                        {result.columns.map((c) => <th key={c}>{c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, i) => (
                        <tr key={`r-${i}-${String(row[0]).slice(0,12)}`}>
                          {row.map((v, j) => (
                            <td key={`c-${i}-${j}`} className={typeof v === "number" ? "amount" : ""}>
                              {v === null || v === "" ? <span className="muted">—</span> : v}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="wb-ok">✓ Statement executed. No result set.</div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
